import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import Swal from 'sweetalert2'
import { apiGet, apiPut } from 'utils/apiFetch'
import apiPath from 'utils/apiPath'
import { AiFillEye } from 'react-icons/ai'
import { FaCheck, FaTimes } from 'react-icons/fa'
import Pagination from '../Pagination'
import {
    fileUrl,
    formatDate,
    isImageFile,
    groupCertificates,
    expirySummary
} from './trainingHelpers.js'

const PAGE_SIZE = 10

const badgeBase = 'inline-block px-2.5 py-0.5 text-xs font-medium rounded-full border whitespace-nowrap'

function expiryBadgeClass(label) {
    if (label === 'Expired') return 'bg-red-50 text-red-700 border-red-200'
    if (label === 'Expiring') return 'bg-amber-50 text-amber-700 border-amber-200'
    if (label === 'Valid') return 'bg-green-50 text-green-700 border-green-200'
    return 'bg-slate-100 text-slate-600 border-slate-200'
}

function verificationBadgeClass(status) {
    if (status === 'verified') return 'bg-green-50 text-green-700 border-green-200'
    if (status === 'rejected') return 'bg-red-50 text-red-700 border-red-200'
    return 'bg-amber-50 text-amber-700 border-amber-200'
}

function DetailItem({ label, value }) {
    return (
        <div>
            <p className='text-xs uppercase tracking-wide text-gray-500'>{label}</p>
            <p className='mt-0.5 text-sm font-medium text-slate-900 break-words'>{value || '—'}</p>
        </div>
    )
}

function CertificationTab({ searchTerm, sortType, filterData }) {
    const [records, setRecords] = useState([])
    const [loading, setLoading] = useState(true)
    const [selectedGroup, setSelectedGroup] = useState(null)
    const [previewDoc, setPreviewDoc] = useState(null)
    const [page, setPage] = useState(1)

    const loadRecords = () => {
        setLoading(true)
        const pageSize = 100
        const loadPage = (page, collected) => apiGet(apiPath.trainingCertificationList, {
            page,
            pageSize
        }).then((result) => {
            const payload = result?.data?.success ? result.data.results || {} : {}
            const docs = payload.docs || []
            const merged = collected.concat(docs)
            const total = Number(payload.totalDocs) || merged.length
            if (merged.length < total && docs.length && page < 20) return loadPage(page + 1, merged)
            return merged
        })
        loadPage(1, [])
            .then((docs) => {
                setRecords(docs)
                setSelectedGroup((current) => {
                    if (!current) return current
                    const documents = docs.filter((item) => String(item.userId || item.employeeCode || item.employeeName) === current.key)
                    return documents.length ? { ...current, documents } : null
                })
            })
            .catch(() => setRecords([]))
            .finally(() => setLoading(false))
    }

    // loads every time the Certification tab is opened (it mounts)
    useEffect(() => {
        loadRecords()
    }, [])

    // go back to the first page whenever a filter changes
    useEffect(() => {
        setPage(1)
    }, [searchTerm, sortType, filterData.startDate, filterData.endDate, filterData.verification])

    const certificateMatches = (item) => {
        if (filterData.verification && item.verification !== filterData.verification) return false
        const recordDate = item.issueDate
        if (filterData.startDate && recordDate && dayjs(recordDate).isBefore(dayjs(filterData.startDate), 'day')) return false
        if (filterData.endDate && recordDate && dayjs(recordDate).isAfter(dayjs(filterData.endDate), 'day')) return false
        return true
    }

    const certificateGroups = groupCertificates(records.filter((item) => item.type === 'certification'))
        .filter((group) => {
            const query = searchTerm.trim().toLowerCase()
            if (query) {
                const name = `${group.employeeName} ${group.employeeCode}`.toLowerCase()
                if (!name.includes(query)) return false
            }
            return group.documents.some(certificateMatches)
        })
        .sort((a, b) => {
            if (!sortType) return 0
            const left = a.employeeName.toLowerCase()
            const right = b.employeeName.toLowerCase()
            if (left === right) return 0
            const order = left > right ? 1 : -1
            return sortType === 'desc' ? -order : order
        })

    // client-side pagination (all records are already loaded)
    const totalItems = certificateGroups.length
    const pageCount = Math.max(1, Math.ceil(totalItems / PAGE_SIZE))
    const currentPage = Math.min(page, pageCount)
    const startIndex = (currentPage - 1) * PAGE_SIZE
    const pagedGroups = certificateGroups.slice(startIndex, startIndex + PAGE_SIZE)
    const paginationOptions = {
        page: currentPage,
        pageCount,
        pageRangeDisplayed: 10,
        totalItems,
        perPageItem: pagedGroups.length
    }

    const setVerification = async (item, verification) => {
        if (!item?._id || item.type !== 'certification') return
        if (item.verification === 'verified' || item.verification === 'rejected') return
        const confirmed = await Swal.fire({
            title: 'Are you sure?',
            text: verification === 'verified' ? 'Mark this as verified?' : 'Mark this as rejected?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            didOpen: () => {
                const container = Swal.getContainer()
                if (container) container.style.zIndex = '20000'
            }
        })
        if (!confirmed.isConfirmed) return
        try {
            const result = await apiPut(
                `${apiPath.trainingCertificationList}/verification/${item._id}`,
                { verification }
            )
            if (!result?.data?.success) {
                Swal.fire({ icon: 'error', title: result?.data?.message || 'Could not update verification' })
                return
            }
            const updated = result.data.results || { ...item, verification }
            setRecords((rows) => rows.map((row) => (row._id === updated._id ? { ...row, ...updated } : row)))
            setSelectedGroup((current) => {
                if (!current) return current
                return {
                    ...current,
                    documents: current.documents.map((row) => (row._id === updated._id ? { ...row, ...updated } : row))
                }
            })
            setPreviewDoc((current) => (current && current._id === updated._id ? { ...current, ...updated } : current))
            Swal.fire({
                icon: 'success',
                title: verification === 'verified' ? 'Marked verified' : 'Marked rejected',
                timer: 1500,
                showConfirmButton: false
            })
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Could not update verification' })
        }
    }

    const closeModal = () => {
        setSelectedGroup(null)
        setPreviewDoc(null)
    }

    return (
        <>
            <div className='p-3'>
                <div className='overflow-x-auto rounded-lg border relative'>
                    <table className='w-full text-xs text-left text-[#6c6c6c]'>
                        <thead className='text-xs text-gray-900 border border-[#E1E6EE] bg-[#E1E6EE]'>
                            <tr>
                                <th className='py-3 px-4 whitespace-nowrap'>Sr. No.</th>
                                <th className='py-3 px-4 whitespace-nowrap'>Employee ID</th>
                                <th className='py-3 px-4 whitespace-nowrap'>Employee Name</th>
                                <th className='py-3 px-4 whitespace-nowrap'>Job Title</th>
                                <th className='py-3 px-4 text-center whitespace-nowrap'>Documents</th>
                                <th className='py-3 px-4 whitespace-nowrap'>Expiry</th>
                                <th className='py-3 px-4 text-center whitespace-nowrap'>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pagedGroups.length ? pagedGroups.map((group, index) => {
                                const expiry = expirySummary(group.documents)
                                return (
                                    <tr key={group.key} className='bg-white border-b hover:bg-gray-50'>
                                        <th scope='row' className='py-4 px-4 border-r font-medium text-gray-900'>
                                            {startIndex + index + 1}
                                        </th>
                                        <td className='py-4 px-4 border-r text-gray-900'>{group.employeeCode || '—'}</td>
                                        <td className='py-4 px-4 border-r text-gray-900'>{group.employeeName || '—'}</td>
                                        <td className='py-4 px-4 border-r text-gray-900'>{group.jobTitle || '—'}</td>
                                        <td className='py-4 px-4 border-r text-center text-gray-900'>{group.documents.length}</td>
                                        <td className='py-4 px-4 border-r'>
                                            <span className={`${badgeBase} ${expiryBadgeClass(expiry)}`}>{expiry}</span>
                                        </td>
                                        <td className='py-4 px-4 text-center'>
                                            <button
                                                type='button'
                                                className='text-[#2f5bff] hover:text-blue-800'
                                                title='View documents'
                                                aria-label='View documents'
                                                onClick={() => {
                                                    setPreviewDoc(null)
                                                    setSelectedGroup(group)
                                                }}
                                            >
                                                <AiFillEye className='w-5 h-5' />
                                            </button>
                                        </td>
                                    </tr>
                                )
                            }) : (
                                <tr>
                                    <td className='py-6 px-4 text-center text-gray-500' colSpan={7}>
                                        {loading ? 'Loading…' : 'No matching records.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {totalItems > 0 && (
                    <Pagination
                        page={currentPage}
                        handlePageClick={(event) => setPage(event.selected + 1)}
                        options={paginationOptions}
                    />
                )}
            </div>

            {/* -------------------- GROUPED CERTIFICATES MODAL -------------------- */}
            {selectedGroup && (
                <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4'>
                    <div className='bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto'>
                        <div className='flex items-start justify-between p-6 border-b'>
                            <div>
                                <h3 className='text-lg font-semibold text-slate-900'>
                                    {selectedGroup.employeeName || 'Employee'}
                                    {selectedGroup.employeeCode ? ` (${selectedGroup.employeeCode})` : ''}
                                </h3>
                                <p className='text-sm text-gray-500 mt-1'>
                                    {selectedGroup.jobTitle || '—'} · {selectedGroup.documents.length} document{selectedGroup.documents.length === 1 ? '' : 's'}
                                </p>
                            </div>
                            <button
                                type='button'
                                onClick={closeModal}
                                className='text-gray-400 hover:text-gray-600 focus:outline-none'
                                aria-label='Close'
                            >
                                <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M6 18L18 6M6 6l12 12' />
                                </svg>
                            </button>
                        </div>
                        <div className='p-4'>
                            <div className='overflow-x-auto rounded-lg border'>
                                <table className='w-full text-sm text-left text-gray-700'>
                                    <thead className='bg-[#E1E6EE] text-gray-900'>
                                        <tr>
                                            <th className='py-3 px-4 whitespace-nowrap'>Sr. No.</th>
                                            <th className='py-3 px-4'>Name</th>
                                            <th className='py-3 px-4'>File</th>
                                            <th className='py-3 px-4'>Verification</th>
                                            <th className='py-3 px-4'>Expiry</th>
                                            <th className='py-3 px-4 text-center'>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedGroup.documents.map((item, index) => {
                                            const verification = item.verification || 'pending'
                                            const expiry = item.expiryTracking || 'No expiry'
                                            const isActive = previewDoc?._id === item._id
                                            return (
                                                <tr key={item._id} className={`border-b ${isActive ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                                                    <td className='py-3 px-4 font-medium text-gray-900'>{index + 1}</td>
                                                    <td className='py-3 px-4'>{item.certificate || '—'}</td>
                                                    <td className='py-3 px-4 break-all'>{item.originalName || '—'}</td>
                                                    <td className='py-3 px-4'>
                                                        <span className={`${badgeBase} capitalize ${verificationBadgeClass(verification)}`}>{verification}</span>
                                                    </td>
                                                    <td className='py-3 px-4'>
                                                        <span className={`${badgeBase} ${expiryBadgeClass(expiry)}`}>{expiry}</span>
                                                    </td>
                                                    <td className='py-3 px-4'>
                                                        <div className='flex items-center justify-center gap-2'>
                                                            <button
                                                                type='button'
                                                                className='text-[#2f5bff] hover:text-blue-800'
                                                                title='View'
                                                                aria-label='View'
                                                                onClick={() => setPreviewDoc(item)}
                                                            >
                                                                <AiFillEye className='w-5 h-5' />
                                                            </button>
                                                            {verification !== 'verified' && verification !== 'rejected' && (
                                                                <>
                                                                    <button
                                                                        type='button'
                                                                        onClick={() => setVerification(item, 'verified')}
                                                                        title='Mark verified'
                                                                        aria-label='Mark verified'
                                                                        className='w-8 h-8 inline-flex items-center justify-center rounded bg-[#2f5bff] text-white hover:bg-blue-700'
                                                                    >
                                                                        <FaCheck className='w-3 h-3' />
                                                                    </button>
                                                                    <button
                                                                        type='button'
                                                                        onClick={() => setVerification(item, 'rejected')}
                                                                        title='Mark rejected'
                                                                        aria-label='Mark rejected'
                                                                        className='w-8 h-8 inline-flex items-center justify-center rounded border border-red-500 text-red-600 hover:bg-red-50'
                                                                    >
                                                                        <FaTimes className='w-3 h-3' />
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        {previewDoc && (
                            <div className='px-6 pb-6'>
                                <div className='rounded-lg border bg-gray-50 p-5'>
                                    <h4 className='text-sm font-semibold text-slate-900 mb-4'>Certificate details</h4>
                                    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
                                        <DetailItem label='Name' value={previewDoc.certificate} />
                                        <DetailItem label='Number' value={previewDoc.number} />
                                        <DetailItem label='Issuer' value={previewDoc.issuer} />
                                        <DetailItem label='Issue date' value={formatDate(previewDoc.issueDate)} />
                                        <DetailItem label='Expiry date' value={formatDate(previewDoc.expiryDate)} />
                                    </div>
                                    {fileUrl(previewDoc) && isImageFile(previewDoc) && (
                                        <img src={fileUrl(previewDoc)} alt={previewDoc.originalName || previewDoc.certificate || 'Certificate'} className='mt-5 max-h-[360px] rounded border bg-white' />
                                    )}
                                    {fileUrl(previewDoc) && !isImageFile(previewDoc) && (
                                        <iframe title={previewDoc.certificate || 'Certificate'} src={fileUrl(previewDoc)} className='mt-5 w-full h-[360px] rounded border bg-white' />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    )
}

export default CertificationTab